
import { useState, useEffect, useRef } from 'react';
import { useEditor as useTiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { CustomImage } from './ImageExtension';

interface UseEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const useEditor = ({ value, onChange, placeholder = 'Start writing...' }: UseEditorProps) => {
  // Use a ref to track whether the content update is coming from external props
  const isExternalUpdate = useRef(false);
  const previousValueRef = useRef(value);

  try {
    const editor = useTiptapEditor({
      extensions: [
        StarterKit.configure({
          // We don't need the default image extension
        }),
        Link.configure({
          openOnClick: false,
        }),
        Placeholder.configure({
          placeholder,
        }),
        // Add our custom image extension 
        CustomImage,
      ],
      content: value,
      onUpdate: ({ editor }) => {
        // Only trigger onChange when the update is from user input, not from props
        if (!isExternalUpdate.current) {
          const html = editor.getHTML();
          onChange(html);
        }
      },
      editorProps: {
        attributes: {
          class: 'focus:outline-none',
        },
      },
    });
    
    // Update editor content when value prop changes (for external updates)
    useEffect(() => {
      if (editor && value !== previousValueRef.current) {
        try {
          // Set the flag to prevent triggering onChange during external updates
          isExternalUpdate.current = true;
          
          // Use setTimeout to avoid React lifecycle issues
          const timeout = setTimeout(() => {
            // Only update if necessary and maintain selection when possible
            if (editor.view && editor.view.state) {
              const currentSelection = editor.view.state.selection;
              editor.commands.setContent(value, false, { preserveWhitespace: 'full' });
              
              // Try to restore selection if possible
              try {
                if (currentSelection) {
                  editor.commands.setTextSelection(currentSelection.from);
                }
              } catch (e) {
                // Silently fail if we can't restore selection
                console.warn('Could not restore editor selection', e);
              }
            }
            
            isExternalUpdate.current = false;
          }, 0);
          
          previousValueRef.current = value;
          
          return () => clearTimeout(timeout);
        } catch (error) {
          console.error("Error updating editor content:", error);
          isExternalUpdate.current = false;
        }
      }
    }, [value, editor]);

    return editor;
  } catch (error) {
    console.error("Error initializing editor:", error);
    return null;
  }
};
